import type { UnifiedAuditRow } from '@/api/console';
import { Drawer, StatusTag } from '@/components/ui';
import { ACTION_LABELS, RESULT_STATUS_CONFIG } from './actionLabels';
import { ActionCell, ActorCell, TargetCell } from './auditCells';

interface Props {
    row: UnifiedAuditRow | null;
    onClose: () => void;
}

/**
 * EAA-1821: `UnifiedAuditRow.detail` is already `JSON.stringify`-ed by the
 * mapper (`toUnifiedFromAccountAudit`/`toUnifiedFromAdminAudit` in
 * `@/api/console`) from each source's raw `detail: Record<string, unknown>`.
 * It is genuine data the list fetch already loaded — not a structured
 * before/after payload, just whatever the write path happened to record —
 * so it is shown as labelled raw JSON, never presented as "the diff". An
 * empty/absent/unparsable value returns `null` so the caller renders the
 * explicit "no before/after data" state instead of blank space.
 */
function parseDetail(raw: string | undefined): Record<string, unknown> | null {
    if (!raw) return null;
    try {
        const parsed: unknown = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
            return parsed as Record<string, unknown>;
        }
        return null;
    } catch {
        return null;
    }
}

/**
 * EAA-1821 (frame 53, honest subset): "Chi tiết một bản ghi nhật ký" —
 * `docs/design/handoff/UX-SPEC.md` L585-588 specs actor, target, tổ chức,
 * source IP/agent, request id, a before/after diff, and **every other event
 * sharing that request id**. None of the last four exist today:
 *
 * - There is no `GET /audit/<id>` — this drawer is populated ENTIRELY from
 *   the row the list already fetched (same "client-side drill-in" trade-off
 *   as `AccountDetailDrawer`), so nothing here is a fresh fetch.
 * - `UnifiedAuditRow`, `AuditRow`, and `AdminAuditRow` (`@/api/console`) have
 *   no `request_id` field at all — "Mã yêu cầu" and the "cùng mã yêu cầu"
 *   event list are both dropped as explicit unavailable states rather than
 *   grouping same-page rows by a field that does not exist.
 * - Tổ chức and "Nguồn" (IP/user agent) are likewise absent from both raw
 *   row shapes — not shown, rather than guessed.
 * - Actor/target render as `sub`/target id only (never a real name) per the
 *   privacy rule already applied by `AuditTable`'s own cells — the
 *   projection has no name field to expand to even if that rule allowed it.
 *
 * What the row DOES carry — timestamp, actor sub, action, target, outcome,
 * and the raw `detail` blob — is rendered in full, reusing `ActionCell` /
 * `TargetCell` (from `./auditCells`) / `CopyableId` so this never
 * re-implements formatting that `AuditTable.tsx` already has.
 */
export function AuditDetailDrawer({ row, onClose }: Props) {
    const detail = row ? parseDetail(row.detail) : null;
    const title = row ? `${ACTION_LABELS[row.action] ?? row.action} — ${row.target}` : '';

    return (
        <Drawer open={Boolean(row)} onClose={onClose} title={title}>
            {row && (
                <div className="portal-accounts-detail">
                    <dl className="portal-accounts-detail-grid">
                        <dt>Thời điểm</dt>
                        <dd>{new Date(row.created_at).toLocaleString('vi-VN')}</dd>

                        <dt>Người thao tác</dt>
                        <dd>
                            <ActorCell row={row} />
                        </dd>

                        <dt>Hành động</dt>
                        <dd>
                            <ActionCell action={row.action} />
                        </dd>

                        <dt>Đối tượng</dt>
                        <dd>
                            <TargetCell row={row} />
                        </dd>

                        <dt>Kết quả</dt>
                        <dd>
                            <StatusTag
                                status={row.success ? 'success' : 'failure'}
                                config={RESULT_STATUS_CONFIG}
                            />
                        </dd>

                        <dt>Mã yêu cầu</dt>
                        <dd className="portal-accounts-unavailable-cell">
                            Không có dữ liệu — bản ghi này không có mã yêu cầu.
                        </dd>
                    </dl>

                    <p className="portal-admin-hint">
                        Tổ chức, nguồn (địa chỉ IP/trình duyệt) và danh sách các thao tác cùng mã
                        yêu cầu chưa có trong dữ liệu portal-api cho bản ghi này — không có trường{' '}
                        <code>request_id</code> trên bản ghi và không có API tra riêng một bản ghi
                        nhật ký, nên các mục này không hiển thị thay vì hiển thị sai.
                    </p>

                    <div className="portal-accounts-detail" style={{ gap: 'var(--space-2)' }}>
                        <span style={{ fontWeight: 600 }}>Trước / Sau</span>
                        {detail ? (
                            <>
                                <p className="portal-admin-hint" style={{ margin: 0 }}>
                                    Bản ghi này chưa có định dạng "trước/sau" chuẩn — dữ liệu thô
                                    dưới đây là trường <code>detail</code> mà máy chủ trả về kèm bản
                                    ghi, không phải một bảng so sánh đã được xử lý.
                                </p>
                                <pre
                                    style={{
                                        margin: 0,
                                        maxHeight: 240,
                                        overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontFamily: 'var(--font-mono)',
                                        fontSize: 12,
                                        background: 'var(--color-surface-sunken)',
                                        padding: 'var(--space-3)',
                                        borderRadius: 'var(--radius-sm)',
                                    }}
                                >
                                    <code>{JSON.stringify(detail, null, 2)}</code>
                                </pre>
                            </>
                        ) : (
                            <p style={{ margin: 0 }}>Không có dữ liệu trước/sau cho bản ghi này.</p>
                        )}
                    </div>

                    <p className="portal-admin-hint" style={{ margin: 0 }}>
                        Bản ghi không sửa và không xoá được.
                    </p>
                </div>
            )}
        </Drawer>
    );
}

export default AuditDetailDrawer;
